import gql from 'graphql-tag';

import { EditableEvent } from 'types/event.types';

export interface GetEditEventQueryResult {
  event: EditableEvent;
}

export const EDIT_EVENT_SESSIONS_FRAGMENT = gql`
  fragment EditEventSessionsFragment on Event {
    __typename # This is required to make the fragment work
    sessions {
      id
      title
      description
      datetimeScheduled
      datetimeEnd
      allDay
      isInPerson
      companyLocationId
      isVirtual
      hasStarted
      hasEnded
      physicalLocation
      virtualLocation
      virtualLocationHost
      hasUnlimitedSpots
      maxAttendees
      targetAttendance
      hasZoomMeeting
      hasGoogleMeeting
      hasZoomParticipants
      hasMeetParticipants
      isSelfCheckinEditable
      guestListTotals {
        confirmed
        incomplete
        waitlisted
      }
      plusOneConfig {
        allowPlusOnes
        maxPlusOnes
        collectQuestionnaireResponses
        maxRegisteredPlusOnes
      }
    }
  }
`;

export const EDIT_EVENT_QUERY = gql`
  query editEvent($id: Int!) {
    event(id: $id) {
      id
      title
      organization {
        id
      }
      isPrivate
      description
      hostOrganizationId
      companyLocationId
      maxAttendees
      allowPlusOnes
      contactName
      contactEmail
      virtualLocation
      virtualLocationHost
      physicalLocation
      allDay
      datetimeScheduled
      createdAt
      editedAt
      createdBy {
        firstName
        lastName
      }
      datetimeEnd
      registrationCutoffAt
      questionnaireCutoffAt
      timezoneCode
      calendarNotifications
      transparency
      slackNotifications
      slackReminders {
        id
        message
        title
        datetime
        notifyStatus
        schedule {
          quantity
          unit
          beforeEventBegins
        }
        selectedGuestStatuses
      }
      emailNotifications
      emailReminders {
        id
        subject
        message
        datetime
        notifyStatus
        selectedGuestStatuses
        schedule {
          quantity
          unit
          beforeEventBegins
        }
      }
      preeventEnable
      preEventUseTemplate
      preEventTemplateId
      preEventTemplate {
        id
        title
      }
      preEventQuestions {
        id
        label
        type
        options
        hasOtherOption
        sort
        isRequired
        isDeleted
        shownBefore
        properties {
          scaleMinValue
          scaleMaxValue
          scaleMinLabel
          scaleMaxLabel
        }
      }
      feedbackEnable
      feedbackUseTemplate
      feedbackTemplateId
      feedbackTemplate {
        id
        title
      }
      feedbackSchedule {
        quantity
        unit
        beforeEventEnds
        sendAt
      }
      isFeedbackAnonymous
      postQuestions {
        id
        label
        type
        options
        hasOtherOption
        sort
        isRequired
        isDeleted
        shownBefore
        properties {
          scaleMinValue
          scaleMaxValue
          scaleMinLabel
          scaleMaxLabel
        }
      }
      slackChannelName
      slackChannelDate
      hasStarted
      hasEnded
      isFeedbackEditable
      slackChannelId
      slackTeamId
      slackChannelAutoarchive
      allowPlusOnes
      listPrivacy
      createSlackChannel
      photoUrl
      originalPhotoUrl
      photoXCrop
      photoYCrop
      photoZoom
      guestListTotals {
        confirmed
        invited
        cancelled
        incomplete
        interested
        waitlisted
        pending
        attended
      }
      targetAttendance
      slackPhotoUrl
      thumbnailUrl
      status
      isPublished
      isVirtual
      virtualEventInfo {
        hasUnlimitedSpots
      }
      hasZoomMeeting
      hasGoogleMeeting
      hasZoomParticipants
      hasMeetParticipants
      isInPerson
      tags
      usesCreatorsCalendar
      userInviteeCount
      distrolistInvitees {
        id
        name
        memberCount
      }
      ...EditEventSessionsFragment
      draftData
      isInviteOn
      sendCalendarInvites
      sendSlackInvites
      allowUserCheckin
      sendUserCheckinEmail
      sendUserCheckinSlack
      sendUserCheckinSchedule {
        quantity
        unit
        beforeEventBegins
      }
      isSelfCheckinEditable
      isArchived
      eventType
      budget
      hasOutlookCalendarEvent
      limitRegistration
      isEventSplitAcrossCalendarEvents
      t2DomainArray
      plusOneConfig {
        allowPlusOnes
        maxPlusOnes
        collectQuestionnaireResponses
        maxRegisteredPlusOnes
      }
    }
  }
  ${EDIT_EVENT_SESSIONS_FRAGMENT}
`;

export const EDIT_EVENT_QUESTIONS_QUERY = gql`
  query editEvent($id: Int!) {
    questions(eventId: $id) {
      id
      eventId
      label
      type
      options
      hasOtherOption
      sort
      isRequired
      isDeleted
      shownBefore
    }
  }
`;

export const EDIT_EVENT_CHANGES_QUERY = gql`
  query eventChanges(
    $id: Int!
    $title: String
    $description: String
    $isVirtual: Boolean
    $isInPerson: Boolean
    $virtualLocation: String
    $companyLocationId: Int
    $physicalLocation: String
    $allDay: Boolean
    $datetimeScheduled: String
    $datetimeEnd: String
    $sessions: [EventSessionInput]!
  ) {
    eventChanges(
      changes: {
        id: $id
        title: $title
        description: $description
        isVirtual: $isVirtual
        isInPerson: $isInPerson
        virtualLocation: $virtualLocation
        companyLocationId: $companyLocationId
        physicalLocation: $physicalLocation
        allDay: $allDay
        datetimeScheduled: $datetimeScheduled
        datetimeEnd: $datetimeEnd
        sessions: $sessions
      }
    ) {
      areEligibleToSendNotifications
      isStartDatetimeChanging
      isEndDatetimeChanging
      changedSessionsCount
      isTitleChanging
      isDescriptionChanging
      isLocationChanging
      isVirtualLinkChanging
    }
  }
`;
